import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Download, FileText, Loader2, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";

const FLOW = ["Draft", "Submitted", "Approved", "Ordered", "Received"];
const CHIP = {
  Draft: "bg-[#F3F4F6] text-[#374151]", Submitted: "bg-[#EFF6FF] text-[#002FA7]",
  Approved: "bg-[#ECFDF5] text-[#2E7D32]", Ordered: "bg-[#FFFBEB] text-[#B45309]",
  Received: "bg-[#ECFDF5] text-[#2E7D32]", Cancelled: "bg-[#FEF2F2] text-[#C92A2A]",
};

export default function IndentPlanner() {
  const [rows, setRows] = useState([]);
  const [qty, setQty] = useState({});
  const [picked, setPicked] = useState({});
  const [indents, setIndents] = useState([]);
  const [notes, setNotes] = useState("");
  const [requiredBy, setRequiredBy] = useState("");
  const [busy, setBusy] = useState(false);
  const [pdf, setPdf] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, i] = await Promise.all([api.get("/store/indents/suggestions"), api.get("/store/indents")]);
      setRows(s.data);
      setIndents(i.data);
      setQty(Object.fromEntries(s.data.map((r) => [r.item_id, r.suggested_qty || ""])));
      setPicked({});
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const lines = rows
      .filter((r) => picked[r.item_id] && Number(qty[r.item_id]) > 0)
      .map((r) => ({ item_id: r.item_id, quantity: Number(qty[r.item_id]), remarks: "" }));
    if (lines.length === 0) return toast.error("Tick the items you want and set a quantity");
    setBusy(true);
    try {
      const { data } = await api.post("/store/indents", { lines, notes, required_by: requiredBy, priority: "Routine" });
      toast.success(`${data.indent_no} created with ${lines.length} items`);
      setNotes(""); setRequiredBy("");
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const advance = async (ind, status) => {
    try {
      const { data } = await api.put(`/store/indents/${ind.indent_id}/status`, null, { params: { status } });
      toast.success(status === "Received"
        ? `${ind.indent_no} received — ${data.stock_receipts_created} stock receipts posted`
        : `${ind.indent_no} marked ${status}`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (ind) => {
    if (!window.confirm(`Delete ${ind.indent_no}?`)) return;
    try {
      await api.delete(`/store/indents/${ind.indent_id}`);
      toast.success("Indent deleted");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const base = process.env.REACT_APP_BACKEND_URL;

  return (
    <div data-testid="indent-planner-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">Indent Planner</h1>
      <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
        Build a multi-item procurement request straight from the item master. Suggested quantities top each
        item back up to its maximum level; receiving an approved indent posts the stock receipts for you.
      </p>

      <div className="mt-6 rounded-md border border-[#E5E7EB] bg-white overflow-x-auto">
        <div className="px-4 py-3 border-b border-[#E5E7EB]">
          <h2 className="font-heading font-bold text-sm text-[#111827]">Reorder suggestions</h2>
          <p className="text-xs text-[#6B7280] mt-0.5">
            Items at or below their reorder level, or short of maximum stock. Tick the ones you want to order.
          </p>
        </div>
        <table className="w-full text-sm" data-testid="suggestions-table">
          <thead className="bg-[#F9FAFB]">
            <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">
              <th className="px-4 py-2.5 w-10">
                <input type="checkbox" data-testid="suggestion-select-all"
                  checked={rows.length > 0 && rows.every((r) => picked[r.item_id])}
                  onChange={(e) => setPicked(e.target.checked
                    ? Object.fromEntries(rows.map((r) => [r.item_id, true])) : {})} />
              </th>
              <th className="px-4 py-2.5">Item</th>
              <th className="px-4 py-2.5 text-right">Current</th>
              <th className="px-4 py-2.5 text-right">Reorder</th>
              <th className="px-4 py-2.5 text-right">Maximum</th>
              <th className="px-4 py-2.5 text-right">Order qty</th>
              <th className="px-4 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6B7280]" data-testid="suggestions-empty">
                Every item is at or above its maximum level — nothing to indent.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.item_id} className="hover:bg-[#F9FAFB]" data-testid={`suggestion-${r.code}`}>
                <td className="px-4 py-2.5">
                  <input type="checkbox" checked={!!picked[r.item_id]} data-testid={`suggestion-pick-${r.code}`}
                    onChange={(e) => setPicked((p) => ({ ...p, [r.item_id]: e.target.checked }))} />
                </td>
                <td className="px-4 py-2.5">
                  <p className="text-[#111827]">{r.generic_name}</p>
                  <p className="text-xs text-[#6B7280]">{r.code} · {r.preferred_vendor || "no vendor set"}</p>
                </td>
                <td className="px-4 py-2.5 text-right">{r.on_hand} {r.stock_unit}</td>
                <td className="px-4 py-2.5 text-right text-[#6B7280]">{r.reorder_level}</td>
                <td className="px-4 py-2.5 text-right text-[#6B7280]">{r.max_level}</td>
                <td className="px-4 py-2.5 text-right">
                  <Input value={qty[r.item_id] ?? ""} data-testid={`suggestion-qty-${r.code}`}
                    onChange={(e) => setQty((p) => ({ ...p, [r.item_id]: e.target.value }))}
                    className="h-9 w-24 ml-auto text-right" />
                </td>
                <td className="px-4 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] ${
                    r.stock_status === "Critical" || r.stock_status === "Out of Stock"
                      ? "bg-[#FEF2F2] text-[#C92A2A]" : "bg-[#FFFBEB] text-[#B45309]"}`}>{r.stock_status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-3 border-t border-[#E5E7EB] flex flex-wrap items-end gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">Required by</p>
            <Input type="date" value={requiredBy} onChange={(e) => setRequiredBy(e.target.value)}
              className="mt-1 h-9 w-40" data-testid="indent-required-by" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <p className="text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">Notes</p>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 h-9"
              placeholder="Justification, budget head, urgency…" data-testid="indent-notes" />
          </div>
          <Button onClick={create} disabled={busy} data-testid="create-indent-btn"
            className="bg-[#002FA7] hover:bg-[#00237C] text-white h-9">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> :
              `Create indent (${rows.filter((r) => picked[r.item_id]).length})`}
          </Button>
        </div>
      </div>

      <h2 className="font-heading font-bold text-sm text-[#111827] mt-8">Indents</h2>
      <div className="mt-3 space-y-3">
        {indents.length === 0 && (
          <p className="text-sm text-[#6B7280]" data-testid="indents-empty">No indents raised yet.</p>
        )}
        {indents.map((ind) => {
          const next = FLOW[FLOW.indexOf(ind.status) + 1];
          return (
            <div key={ind.indent_id} className="rounded-md border border-[#E5E7EB] bg-white p-4"
              data-testid={`indent-${ind.indent_no}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div>
                  <p className="font-medium text-[#111827]">{ind.indent_no}</p>
                  <p className="text-xs text-[#6B7280]">
                    {ind.lines.length} items · raised by {ind.raised_by} on {String(ind.created_at).slice(0, 10)}
                    {ind.approved_by ? ` · approved by ${ind.approved_by}` : ""}
                  </p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[11px] ${CHIP[ind.status] || ""}`}
                  data-testid={`indent-status-${ind.indent_no}`}>{ind.status}</span>
                <div className="ml-auto flex flex-wrap gap-2">
                  <button type="button" data-testid={`indent-pdf-${ind.indent_no}`}
                    onClick={() => setPdf({ path: `/store/indents/${ind.indent_id}.pdf`,
                      filename: `Indent_${ind.indent_no}.pdf`, title: `Indent ${ind.indent_no}` })}
                    className="inline-flex items-center h-9 px-3 rounded-md border border-[#E5E7EB] text-xs hover:border-[#002FA7]">
                    <FileText className="h-3.5 w-3.5 mr-1.5 text-[#002FA7]" /> PDF
                  </button>
                  <a href={`${base}/api/store/indents/${ind.indent_id}.csv`}
                    data-testid={`indent-csv-${ind.indent_no}`}
                    className="inline-flex items-center h-9 px-3 rounded-md border border-[#E5E7EB] text-xs hover:border-[#002FA7]">
                    <Download className="h-3.5 w-3.5 mr-1.5 text-[#002FA7]" /> CSV
                  </a>
                  {next && (
                    <Button size="sm" variant="outline" onClick={() => advance(ind, next)}
                      data-testid={`indent-advance-${ind.indent_no}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1.5 text-[#2E7D32]" /> Mark {next}
                    </Button>
                  )}
                  {ind.status !== "Received" && (
                    <button onClick={() => remove(ind)} data-testid={`indent-delete-${ind.indent_no}`}
                      className="p-2 rounded hover:bg-[#F3F4F6] text-[#C92A2A]"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {ind.lines.map((l) => (
                  <span key={l.item_id} className="rounded-full border border-[#E5E7EB] bg-[#F9FAFB] px-2 py-0.5 text-[11px]">
                    {l.item_name} × {l.quantity} {l.stock_unit}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <PdfPreviewDialog open={!!pdf} onClose={() => setPdf(null)} path={pdf?.path}
        filename={pdf?.filename} title={pdf?.title} />
    </div>
  );
}
